import { z } from 'zod';
import { AuditedFields, IsoDateTime, Uuid, Version } from './common.js';
/**
 * Impression et points de préparation (ADR-017, DS-06).
 * Une destination = une imprimante (ou un écran) d'un point de vente ; un
 * ticket cuisine est un texte figé, tracé avec son statut d'impression.
 */
export const PrintDestinationKind = z.enum([
    'KITCHEN',
    'BAR',
    'GRILL',
    'PASTRY',
    'CASHIER',
    'OTHER',
]);
/**
 * - `ESCPOS_NETWORK` : imprimante thermique réseau (port 9100), ESC/POS brut ;
 * - `BROWSER` : le ticket est marqué imprimé et s'imprime depuis le navigateur (POS, cuisine) ;
 * - `NONE` : trace seule (écran cuisine sans papier).
 */
export const PrintDriver = z.enum(['ESCPOS_NETWORK', 'BROWSER', 'NONE']);
export const PaperWidth = z.union([z.literal(80), z.literal(58)]);
export const PrintDestinationSettings = z.object({
    host: z.string().trim().max(120).nullable().default(null),
    port: z.number().int().min(1).max(65535).default(9100),
    width: PaperWidth.default(80),
    copies: z.number().int().min(1).max(3).default(1),
});
export const PrintDestination = z
    .object({
    id: Uuid,
    outletId: Uuid,
    code: z.string(),
    name: z.string(),
    kind: PrintDestinationKind,
    driver: PrintDriver,
    settings: PrintDestinationSettings,
    /** Destination par défaut du point de vente pour les produits sans destination explicite. */
    isDefault: z.boolean(),
    isActive: z.boolean(),
    version: Version,
})
    .merge(AuditedFields);
export const CreatePrintDestinationRequest = z
    .object({
    code: z.string().trim().min(2).max(20),
    name: z.string().trim().min(2).max(80),
    kind: PrintDestinationKind,
    driver: PrintDriver.default('BROWSER'),
    settings: PrintDestinationSettings.partial().optional(),
    isDefault: z.boolean().default(false),
})
    .superRefine((input, ctx) => {
    if (input.driver === 'ESCPOS_NETWORK' && !input.settings?.host) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['settings', 'host'],
            message: 'Adresse de l’imprimante réseau obligatoire',
        });
    }
});
export const UpdatePrintDestinationRequest = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    kind: PrintDestinationKind.optional(),
    driver: PrintDriver.optional(),
    settings: PrintDestinationSettings.partial().optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    version: Version,
});
// ---------------------------------------------------------------------------
// Tickets cuisine
// ---------------------------------------------------------------------------
export const KitchenTicketKind = z.enum(['ORDER', 'CANCEL', 'REPRINT']);
export const KitchenTicketStatus = z.enum(['QUEUED', 'PRINTED', 'FAILED', 'CANCELLED']);
export const KitchenTicketItem = z.object({
    orderItemId: Uuid,
    label: z.string(),
    quantity: z.number(),
    options: z.array(z.string()),
    notes: z.string().nullable(),
    status: z.enum(['PENDING', 'SENT', 'READY', 'SERVED', 'CANCELLED']),
});
export const KitchenTicket = z.object({
    id: Uuid,
    outletId: Uuid,
    orderId: Uuid,
    orderReference: z.string(),
    orderDailyNumber: z.number().int(),
    tableCode: z.string().nullable(),
    /** Nom de la note au comptoir (Horevia Bar, ADR-019). */
    tabName: z.string().nullable().default(null),
    serviceType: z.enum(['DINE_IN', 'TAKEAWAY', 'DELIVERY', 'ROOM_SERVICE', 'COUNTER']),
    /** `null` : aucune destination configurée (ticket à l'écran seulement). */
    destinationId: Uuid.nullable(),
    destinationName: z.string().nullable(),
    kind: KitchenTicketKind,
    /** Rang de l'envoi dans la commande (1 = premier envoi). */
    sequence: z.number().int(),
    /** Texte figé du ticket (largeur de la destination). */
    content: z.string(),
    status: KitchenTicketStatus,
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    printedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    items: z.array(KitchenTicketItem),
});
export const ListKitchenTicketsQuery = z.object({
    outletId: Uuid.optional(),
    destinationId: Uuid.optional(),
    status: KitchenTicketStatus.optional(),
    /** Tickets dont au moins une ligne n'est pas encore servie (écran cuisine). */
    activeOnly: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((value) => value === true || value === 'true'),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
//# sourceMappingURL=printing.js.map